import BadgeCheckIcon from '@heroicons/react/outline/BadgeCheckIcon';
import CogIcon from '@heroicons/react/outline/CogIcon';
import ColorSwatchIcon from '@heroicons/react/outline/ColorSwatchIcon';
import CreditCardIcon from '@heroicons/react/outline/CreditCardIcon';
import DocumentDuplicateIcon from '@heroicons/react/outline/DocumentDuplicateIcon';
import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon';
import InboxInIcon from '@heroicons/react/outline/InboxInIcon';
import PresentationChartBarIcon from '@heroicons/react/outline/PresentationChartBarIcon';
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon';
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon';
import TagIcon from '@heroicons/react/outline/TagIcon';
import TemplateIcon from '@heroicons/react/outline/TemplateIcon';
import UserGroupIcon from '@heroicons/react/outline/UserGroupIcon';
import UserIcon from '@heroicons/react/outline/UserIcon';
import UsersIcon from '@heroicons/react/outline/UsersIcon';
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon';
import { useDecision } from '@optimizely/react-sdk';
import FontSizeIcon from 'remixicon-react/FontSizeIcon';
import { useAuthMember } from '@tribeplatform/react-sdk/hooks';
import { FeaturesList } from '../../common/constants/features';
import { useI18n } from '../../i18n';
import { emptyCommandBarResult } from '../types';
export const useCommandBarAdminSettings = () => {
    const intl = useI18n();
    const { isLoading, isAdmin } = useAuthMember();
    const [decisionPostTypes] = useDecision(FeaturesList.PostTypes);
    const categoryTexts = {
        managePeople: intl.$t({
            id: 'CommandBar.AdminSettings.ManagePeople',
            defaultMessage: 'Manage people',
        }),
        manageContent: intl.$t({
            id: 'CommandBar.AdminSettings.ManageContent',
            defaultMessage: 'Manage content',
        }),
        administration: intl.$t({
            id: 'Generics.Administration',
            defaultMessage: 'Administration',
        }),
        customize: intl.$t({
            id: 'Generics.Customize',
            defaultMessage: 'Customize',
        }),
        analytics: intl.$t({
            id: 'Generics.Analytics',
            defaultMessage: 'Analytics',
        }),
        apps: intl.$t({
            id: 'Generics.Apps',
            defaultMessage: 'Apps',
        }),
    };
    if (!isAdmin)
        return emptyCommandBarResult;
    return {
        isLoading,
        actions: [
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.ManageMembers',
                    defaultMessage: 'Manage {MEMBERS}',
                }),
                icon: UserIcon,
                url: '/manage/people/members',
                category: categoryTexts.managePeople,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.ManageAdministrators',
                    defaultMessage: 'Manage administrators',
                }),
                icon: UsersIcon,
                url: '/manage/people/admin',
                category: categoryTexts.managePeople,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.ManageInvitations',
                    defaultMessage: 'Manage invitations',
                }),
                icon: InboxInIcon,
                url: '/manage/people/invitations',
                category: categoryTexts.managePeople,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.ManageProfileFeields',
                    defaultMessage: 'Manage profile fields',
                }),
                icon: UserGroupIcon,
                url: '/manage/people/fields',
                category: categoryTexts.managePeople,
            },
            {
                name: intl.$t({
                    id: 'Generics.ManageBadges',
                    defaultMessage: 'Manage badges',
                }),
                icon: BadgeCheckIcon,
                url: '/manage/people/badges',
                category: categoryTexts.managePeople,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.ManagePosts',
                    defaultMessage: 'Manage posts',
                }),
                icon: DocumentTextIcon,
                url: '/manage/content',
                category: categoryTexts.manageContent,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.ManageSpaces',
                    defaultMessage: 'Manage {SPACES}',
                }),
                icon: ViewGridIcon,
                url: '/manage/content/spaces',
                category: categoryTexts.manageContent,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.ManageTags',
                    defaultMessage: 'Manage {TAGS}',
                }),
                icon: TagIcon,
                url: '/manage/content/tags',
                category: categoryTexts.manageContent,
            },
            decisionPostTypes.enabled && {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.ManagePostTypes',
                    defaultMessage: 'Manage post types',
                }),
                icon: DocumentDuplicateIcon,
                url: '/manage/content/post-types',
                category: categoryTexts.manageContent,
            },
            {
                name: intl.$t({
                    id: 'Generics.CustomizeSpaces',
                    defaultMessage: 'Manage customize {SPACES}',
                }),
                icon: DocumentTextIcon,
                url: '/manage/customizer/spaces',
                category: categoryTexts.customize,
            },
            {
                name: intl.$t({
                    id: 'Generics.CustomizeThemeColors',
                    defaultMessage: 'Customize theme colors',
                }),
                icon: ColorSwatchIcon,
                url: '/manage/customizer/colors',
                category: categoryTexts.customize,
            },
            {
                name: intl.$t({
                    id: 'Generics.CustomizeCommunityNavigation',
                    defaultMessage: 'Customize {COMMUNITY} navigation',
                }),
                icon: TemplateIcon,
                url: '/manage/customizer/navigation',
                category: categoryTexts.customize,
            },
            {
                name: intl.$t({
                    id: 'Generics.CustomizeThemeTypography',
                    defaultMessage: 'Customize theme typography',
                }),
                icon: FontSizeIcon,
                url: '/manage/customizer/typography',
                category: categoryTexts.customize,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.CommunitySettings',
                    defaultMessage: '{COMMUNITY} settings',
                }),
                icon: CogIcon,
                url: '/manage/settings/network',
                category: categoryTexts.administration,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.CommunityModerationSettings',
                    defaultMessage: '{COMMUNITY} moderation settings',
                }),
                icon: ShieldCheckIcon,
                url: '/manage/settings/moderation',
                category: categoryTexts.administration,
            },
            {
                name: intl.$t({
                    id: 'Generics.Billing',
                    defaultMessage: 'Billing',
                }),
                icon: CreditCardIcon,
                url: '/manage/settings/billing',
                category: categoryTexts.administration,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.CommunityAnalytics',
                    defaultMessage: '{COMMUNITY} analytics',
                }),
                icon: PresentationChartBarIcon,
                url: '/manage/reports/analytics',
                category: categoryTexts.analytics,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.CommunityAuditLogs',
                    defaultMessage: '{COMMUNITY} audit logs',
                }),
                icon: ShieldExclamationIcon,
                url: '/manage/reports/audit-logs',
                category: categoryTexts.analytics,
            },
            {
                name: intl.$t({
                    id: 'CommandBar.AdminSettings.InstallApps',
                    defaultMessage: 'Install apps',
                }),
                icon: ViewGridIcon,
                url: '/manage/apps',
                category: categoryTexts.apps,
            },
        ],
    };
};
//# sourceMappingURL=useCommandBarAdminSettings.js.map